import { FileSettings, UpdateSettingsRequest, UploadRequest, UploadResponse, MetadataListResponse } from '../types/metadata';
import { getValidAuthHeaders } from '../utils/authHeaders';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Log API configuration on module load
if (!API_BASE_URL) {
  console.warn('[metadataService] ⚠️ REACT_APP_API_URL is not set. API calls will fail. Please set it in .env file.');
} else {
  console.log('[metadataService] 🔗 API Base URL:', API_BASE_URL);
}

export const metadataService = {
  /**
   * Request a presigned upload URL with metadata
   */
  async requestUploadUrl(request: UploadRequest): Promise<UploadResponse> {
    const response = await fetch(`${API_BASE_URL}/upload`, {
      method: 'POST',
      headers: await getValidAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to request upload URL');
    }

    return response.json();
  },

  /**
   * Upload file to S3 using presigned URL
   */
  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type || 'text/csv',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to upload file to S3');
    }
  },

  /**
   * List all files or get specific file versions
   */
  async listMetadata(fileId?: string): Promise<MetadataListResponse> {
    if (!API_BASE_URL) {
      throw new Error('API URL is not configured. Please set REACT_APP_API_URL in your .env file.');
    }

    const url = fileId 
      ? `${API_BASE_URL}/metadata?id=${fileId}`
      : `${API_BASE_URL}/metadata`;

    console.log('[metadataService] 📡 Fetching metadata from:', url);

    const response = await fetch(url, {
      method: 'GET',
      headers: await getValidAuthHeaders(),
    });

    if (!response.ok) {
      let errorMessage = 'Failed to fetch metadata';
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        // Response is not JSON, likely HTML error page
        errorMessage = `API returned ${response.status}: ${response.statusText}. Check if REACT_APP_API_URL is correct.`;
      }
      throw new Error(errorMessage);
    }

    return response.json();
  },

  /**
   * Complete upload workflow: request URL, upload file, return metadata
   */
  async uploadFileWithMetadata(
    file: File,
    metadata: {
      id?: string;
      version?: number;
      type: 'points' | 'polygons';
      name: string;
      pointSettings?: any;
      polygonSettings?: any;
      viewSettings?: any;
    }
  ): Promise<UploadResponse> {
    // Request upload URL
    const uploadResponse = await this.requestUploadUrl({
      metadata: {
        ...metadata,
        name: metadata.name,
      },
      fileName: file.name,
    });

    // Upload file to S3
    await this.uploadFile(uploadResponse.uploadUrl, file);

    return uploadResponse;
  },

  /**
   * Update file settings (affects all versions)
   */
  async updateSettings(request: UpdateSettingsRequest): Promise<FileSettings> {
    const response = await fetch(`${API_BASE_URL}/settings`, {
      method: 'PUT',
      headers: await getValidAuthHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update settings');
    }

    return response.json();
  },
};
